import { CONTACT_CUSTOM_ATTRIBUTE_VARIABLE_PREFIX } from 'dashboard/helper/messageVariableOptions';

const MESSAGE_VARIABLES_REGEX = /{{(.*?)}}/g;
const skipCodeBlocks = str => str.replace(/```(?:.|\n)+?```/g, '');

export const capitalizeName = name => {
  return (name || '').replace(/\b(\w)/g, s => s.toUpperCase());
};

export const getFirstName = ({ user }) => {
  const firstName = user?.name ? user.name.split(' ').shift() : '';
  return capitalizeName(firstName);
};

export const getLastName = ({ user }) => {
  if (user && user.name) {
    const lastName =
      user.name.split(' ').length > 1 ? user.name.split(' ').pop() : '';
    return capitalizeName(lastName);
  }
  return '';
};

// Format time as "3:00 PM"
const formatMeetingTime = dateString => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '';
  }
};

// Get ordinal suffix (1st, 2nd, 3rd, etc.)
const getOrdinalSuffix = day => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

// Format as "Monday at 3:00 PM (2nd Feb, 2026)"
const formatMeetingDateTiming = dateString => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
    const time = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    const day = date.getDate();
    const dayWithSuffix = `${day}${getOrdinalSuffix(day)}`;
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${dayName} at ${time} (${dayWithSuffix} ${month}, ${year})`;
  } catch {
    return '';
  }
};

export const getMessageVariables = ({ conversation }) => {
  const {
    meta: { assignee, sender },
    id,
    inbox,
    account_id,
    meet_link, calendar_timings, demo_meet_timing, reminder_note,
  } = conversation;

  // Get contact's additional_attributes for demo-related fields
  const contactAdditionalAttrs = sender?.additional_attributes || {};
  const demoTiming = contactAdditionalAttrs.demo_timing || '';
  const availableParticipants = Array.isArray(
    contactAdditionalAttrs.available_participants
  )
    ? contactAdditionalAttrs.available_participants
    : [];
  const joinedGuest = contactAdditionalAttrs.joined_guest || {};
  const joinedGuestName = joinedGuest.display_name?.toString?.() || '';
  const joinedGuestEmail = joinedGuest.email?.toString?.() || '';
  const allJoinedGuests = Array.isArray(contactAdditionalAttrs.all_joined_guests)
    ? contactAdditionalAttrs.all_joined_guests
    : [];
  const allJoinedGuestsDisplay = allJoinedGuests
    .map(guest => {
      const name = guest?.display_name?.toString?.() || '';
      const email = guest?.email?.toString?.() || '';
      if (!name && !email) return '';
      return email ? `${name} (${email})` : name;
    })
    .filter(Boolean)
    .join(', ');

  const availableParticipantVariables = availableParticipants.reduce(
    (acc, name, index) => {
      acc[`contact.available_participant_${index + 1}`] = name;
      return acc;
    },
    {}
  );

  const contactCustomAttributeVariables = Object.entries(
    sender?.custom_attributes || {}
  ).reduce((acc, [key, value]) => {
    acc[`${CONTACT_CUSTOM_ATTRIBUTE_VARIABLE_PREFIX}${key}`] =
      value === null || value === undefined ? '' : String(value);
    return acc;
  }, {});

  return {
    'contact.name': capitalizeName(sender?.name || ''),
    'contact.first_name': getFirstName({ user: sender }),
    'contact.last_name': getLastName({ user: sender }),
    'contact.email': sender?.email,
    'contact.phone': sender?.phone_number,
    'contact.id': sender?.id,
    'contact.meeting_timing': formatMeetingTime(demoTiming),
    'contact.meeting_date_timing': formatMeetingDateTiming(demoTiming),
    'contact.demo_subject': contactAdditionalAttrs.demo_subject || '',
    'contact.demo_meeting_link': contactAdditionalAttrs.meeting_link || '',
    'contact.available_participants': availableParticipants.join(', '),
    'contact.joined_guest_name': joinedGuestName,
    'contact.joined_guest_display_name': joinedGuestName,
    'contact.joined_guest_email': joinedGuestEmail,
    'contact.first_joining_time':
      contactAdditionalAttrs.first_joining_time || '',
    'contact.all_joined_guests': allJoinedGuestsDisplay,
    ...availableParticipantVariables,
    'contact.labels': sender?.labels || [],
    'conversation.id': id,
    'agent.name': capitalizeName(assignee?.name || ''),
    'agent.first_name': getFirstName({ user: assignee }),
    'agent.last_name': getLastName({ user: assignee }),
    'agent.email': assignee?.email ?? '',
    'agent.kivo_chat_username': assignee?.kivo_chat_username ?? '',
    'account.id': account_id || '',
    'inbox.type': inbox?.channel_type || '',
    'conversation.meet_link': meet_link,
    'conversation.reminder_note': reminder_note,
    'conversation.meeting_timings':  calendar_timings,
    'conversation.demo_meet_timing':  demo_meet_timing,
    ...contactCustomAttributeVariables,
  };
};

export const replaceVariablesInMessage = ({ message, variables }) => {
  // @ts-ignore
  return message.replace(MESSAGE_VARIABLES_REGEX, (_, replace) => {
    return variables[replace.trim()]
      ? variables[replace.trim().toLowerCase()]
      : '';
  });
};

export const getUndefinedVariablesInMessage = ({ message, variables }) => {
  const messageWithOutCodeBlocks = skipCodeBlocks(message);
  const matches = messageWithOutCodeBlocks.match(MESSAGE_VARIABLES_REGEX);
  if (!matches) return [];

  return matches
    .map(match => {
      return match
        .replace('{{', '')
        .replace('}}', '')
        .trim();
    })
    .filter(variable => {
      return !variables[variable];
    });
};
